import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { newTracker, trackStructEvent } from "@snowplow/browser-tracker";

const sendInformation = async (
  values: any,
  firstName: any,
  lastName: any,
  email: any,
  phone: any,
  city: any,
  howDidYouHear: any,
  howDidYouHearAdditionalText: any,
  message: any,
  receiveInformationEmails: any,
  termsAgreement: any,
  interestedIn: any,
  channelPartner: any,
  channelPartnerId: any,
  leadType: any,
  getCookie: any,
  setSubmissionComplete: any,
  setErrorVisible: any,
  setSubmitting: any,
  AnonymousId: any,
  contactPreference: any
) => {
  newTracker("snowplow", "https://api-v2.us.agorocarbon.com", {});
  let body = {
    firstName: firstName,
    lastName: lastName,
    email: email,
    phone: phone,
    howDidYouHear: howDidYouHear,
    howDidYouHearAdditionalText: howDidYouHearAdditionalText,
    contactPreference: contactPreference,
    county: values?.countyParam,
    state: values?.stateParam,
    country: "United States",
    city: city,
    description: message,
    receiveInformationEmails: receiveInformationEmails,
    channelPartnerName: channelPartner,
    channelPartnerId: channelPartnerId,
    interestedIn: interestedIn,

    leadType: leadType,
    contactFormTermsAgreement: termsAgreement,

    sourceDetails: values?.formName,
    leadSource: "WebsiteCarbonCalculator",

    hidePayout: values?.hidePayout,
    totalAcres: values?.totalAreaParam,

    quote: values.quoteParams,

    utm_source: getCookie("utm_source"),
    utm_medium: getCookie("utm_medium"),
    utm_campaign: getCookie("utm_campaign"),
    utm_term: getCookie("utm_term"),
    utm_content: getCookie("utm_content"),
    anonymousId: AnonymousId,
  };

  try {
    await axios.post(
      `${process.env.REACT_APP_GATEWAY_API_ENDPOINT}/base/website/leads`,
      body
    );

    trackStructEvent({
      category: "User Interaction",
      action: "Submit Form",
      label: "Multi-input Form",
      property: JSON.stringify(body),
    });

    setSubmissionComplete(true);

    let top = 0;
    const rootElement = document.querySelector("#root");
    if (rootElement) {
      top = rootElement.getBoundingClientRect().top + window.scrollY - 140;
    }
    window.scrollTo({
      top: top,
      left: 0,
      behavior: "smooth",
    });
  } catch (e) {
    console.log(e);
    setErrorVisible(true);
  } finally {
    setSubmitting(false);
  }
};

const qualifiedStates = [
  "AL", // Alabama
  "IL", // Illinois
  "AR", // Arkansas
  "IA", // Iowa
  "FL", // Florida
  "KS", // Kansas
  "GA", // Georgia
  "MI", // Michigan
  "LA", // Louisiana
  "MN", // Minnesota
  "MS", // Mississippi
  "MO", // Missouri
  "TN", // Tennessee
  "NE", // Nebraska
  "TX", // Texas
  "NJ", // New Jersey
  "NY", // New York
  "NC", // North Carolina
  "PA", // Pennsylvania
  "ND", // North Dakota
  "OH", // Ohio
  "OK", // Oklahoma
  "SC", // South Carolina
  "VA", // Virgina (corrected to Virginia)
  "SD", // South Dakota
  "WI", // Wisconsin
  "IN", // Indiana
  "KY", // Kentucky
  "WV", // West Virginia
];
const calculateResult = async (
  locations: any,
  hideNitrogenReduction: boolean
) => {
  const body: any = {
    scenarios: [],
  };

  for (const i of locations) {
    const isIrrigated = i.typeOfLand === "irrigated";
    const isIrrigatedRange = i.typeOfLandRange === "irrigated";
    const locationObject = { state: i.state, county: i.county };
    if (i.rowCropArea) {
      const scenario: any = {
        id: uuidv4(),
        name: `Field ${body.scenarios.length + 1}`,
        area: i.rowCropArea,
        details: {
          isLegacy: !!i.practices.legumeAddition,
          isIrrigated: isIrrigated,
          paymentOption: "OPTION_A",
          isUnqualified: qualifiedStates.includes(i.state) === false,
        },
        location: locationObject,
        practiceType: "ROW_CROP",
        systemOfMeasurement: "IMPERIAL",
        practices: [],
      };
      if (i.practices.legumeAddition) {
        scenario.practices.push({
          id: "5902544f-094a-42c0-8b7e-2b9879bba4bb",
          name: "Legume Addition",
          availablePaymentOptions: ["OPTION_A"],
          type: "ROW_CROP",
          shortCode: "LA",
          legacy: "LEGACY",
        });
      }
      if (i.practices.reducedTillageNoTillage) {
        scenario.practices.push({
          id: "202291f1-1331-4c08-bae7-9752b20f838d",
          name: "No Tillage",
          type: "ROW_CROP",
          shortCode: "NT",
          availablePaymentOptions: ["OPTION_A", "OPTION_B"],
          legacy: "CURRENT",
          details: { new: "no", current: "intensive" },
        });
      }
      if (i.practices.coverCropping) {
        scenario.practices.push({
          id: "2bfdb656-c07e-4394-a8de-011778d2b233",
          name: "Cover cropping",
          type: "ROW_CROP",
          shortCode: "CC",
          availablePaymentOptions: ["OPTION_A", "OPTION_B"],
          legacy: "CURRENT",
          details: { isLegume: true },
        });
      }
      if (i.practices.nitrogenReduction) {
        scenario.practices.push({
          id: "42abfebb-0988-4502-ae57-ed61d92cda8b",
          name: "Nitrogen reduction",
          type: "ROW_CROP",
          shortCode: "NRR",
          availablePaymentOptions: ["ANNUAL"],
          legacy: "BOTH",
          details: { yearsEnrolled: 5 },
        });
      }
      if (i.practices.legumeAddition) {
        scenario.practices = scenario.practices.filter(
          (practice: any) =>
            practice.shortCode === "NRR" || practice.shortCode === "LA"
        );
      }
      body.scenarios.push(scenario);
    }
    if (i.pastureLandArea) {
      const scenario: any = {
        id: uuidv4(),
        name: `Field ${body.scenarios.length + 1}`,
        area: i.pastureLandArea,
        details: {
          isLegacy: false,
          isIrrigated: isIrrigatedRange,
          paymentOption: "OPTION_A",
          isUnqualified: qualifiedStates.includes(i.state) === false,
        },
        location: locationObject,
        practiceType: "PASTURELAND",
        systemOfMeasurement: "IMPERIAL",
        practices: [],
      };
      if (i.practices.seeding) {
        scenario.practices.push({
          id: "6a4df17d-16be-40fc-a732-ede8abcfe6ad",
          name: "Biodiversity/Seeding",
          type: "PASTURELAND",
          shortCode: "RPBS",
          availablePaymentOptions: ["OPTION_A", "OPTION_B"],
          legacy: "CURRENT",
        });
      }
      if (i.practices.fertilization) {
        scenario.practices.push({
          id: "cfc05a2d-cec3-497d-a801-1e156ddefb5c",
          name: "Fertilization",
          type: "PASTURELAND",
          shortCode: "RPF",
          availablePaymentOptions: ["OPTION_A", "OPTION_B"],
          legacy: "CURRENT",
        });
      }
      if (i.practices.improvedGrazing) {
        scenario.practices.push({
          id: "da515cb6-f449-4074-a48b-bc710ad66def",
          name: "Improved grazing",
          type: "PASTURELAND",
          shortCode: "RPIG",
          availablePaymentOptions: ["OPTION_A", "OPTION_B"],
          legacy: "CURRENT",
        });
      }
      body.scenarios.push(scenario);
    }
  }
  try {
    const payload = { ...body, country: "US" };
    if (!hideNitrogenReduction) {
      payload.contractVersionId = "d983957a-6072-40bf-ac6a-7bdfffd1c7d2";
    }
    // fix me when removing NRR from website
    const calculationResult = await axios.put(
      `${process.env.REACT_APP_GATEWAY_API_ENDPOINT}/estimate-service/calculator/calculate-all`,
      payload
    );
    return {
      result: calculationResult.data,
      scenarios: calculationResult.data.calculatorResultA.updatedScenarios,
    };
  } catch (e) {
    console.log(e);
  }
};

export const EstimateService = {
  sendInformation,
  calculateResult,
};
